<template>
    <hts-drawer
        v-model="visible"
        size="xl"
        :title="$t('labels.planningModify')"
        :submit="handleSaveShipingPlan"
        @close="close">
        <div>
            <hts-card :loading="loading">
                <template #title>
                    <div class="w-full flex justify-start gap-4 my-2">
                        <span>
                            {{ $t('labels.shippingInformation2') }}
                        </span>
                        <hi-pencil-square-icon
                            v-if="viewShippingPlan || typeSolutionWaitingTimeTaskSelected === 'manualPlanning'"
                            class="w-6 h-6 text-error-500 cursor-pointer"
                            @click="handleEditShippingPlan" />

                        <hi-puzzle-piece-icon
                            v-if="solutionWaitingTimeTask && solutionWaitingTimeTask.length"
                            class="w-6 h-6 text-info-500 cursor-pointer"
                            @click="quickShowModalSolutionWaitingTimeTask" />

                        <div class="flex flex-col border-2 border-dotted pl-2 pb-2 pr-2">
                            <span class="font-normal text-lg text-warning-600">
                                Ghi chú
                            </span>
                            <div class="flex justify-start gap-4 my-2">
                                <div class="flex justify-start gap-4 my-2 items-center">
                                    <date-time
                                        class="w-[90px] min-w-[90px]"
                                        readonly
                                        color="success" />
                                    <span class="font-normal text-lg">
                                        {{ $t('labels.earliestDeadline') }}
                                    </span>
                                </div>
                                <div class="flex justify-start gap-4 my-2 items-center">
                                    <date-time
                                        class="w-[90px] min-w-[90px]"
                                        readonly
                                        color="warning" />
                                    <span class="font-normal text-lg">
                                        {{ $t('labels.latestDeadline') }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <shipping-information
                    key="dataAdd.positions"
                    :positions="dataAdd.positions"
                    :condition="dataAdd.condition"
                    :waiting-tasks="dataAdd.waitingTasks"
                    :edit-travel-time="CEditTravelTime"
                    :disabled="CDisabledEditCompletionTime" />

                <div class="flex justify-start gap-2">
                    <hts-form-button
                        color="info"
                        type="button"
                        :outline="false"
                        :loading="loadShippingPlan"
                        :disabled="viewShippingPlan || typeSolutionWaitingTimeTaskSelected === 'manualPlanning'"
                        @click="handleShowShippingPlan">
                        {{ $t('labels.watchOrderPlan') }}
                    </hts-form-button>

                    <hts-tooltip
                        placement="top"
                        :offset="[5, 5]">
                        <div>
                            <p>
                                {{ $t('labels.taskSettingLabel') }}
                            </p>
                        </div>
                        <template #reference>
                            <hts-form-button
                                color="error"
                                type="button"
                                :outline="false"
                                @click="openConditionSetting">
                                {{ $t('labels.taskSetting') }}
                            </hts-form-button>
                        </template>
                    </hts-tooltip>

                    <hts-form-button
                        color="accent"
                        type="button"
                        :disabled="CDisabledEditCompletionTime"
                        :outline="false"
                        @click="openWaitingTask">
                        <span class="text-white">
                            {{ $t('labels.addWaitingTask') }}
                        </span>
                    </hts-form-button>
                </div>

                <div
                    v-if="typeSolutionWaitingTimeTaskSelected === 'manualPlanning'"
                    class="grid grid-cols-3 gap-3">
                    <hts-form-date
                        v-model="manualPlanning.IN"
                        :label="`Thời gian in điểm ${CPositions?.[0]?.nodeLocationName} (vị trí 1)`"
                        class="w-full"
                        type="datetime"
                        time-format="HH:mm"
                        required
                        :unix-time="true" />
                </div>
            </hts-card>

            <travel-detail ref="travelDetail" />

            <shipping-plan
                ref="shippingPlan"
                v-loading="loading" />

            <!-- Cài đặt điều kiện công việc trước sau -->
            <condition-setting
                ref="conditionSetting"
                @update="(val) => updateConditionSetting(val)" />

            <!-- Cài đặt công việc chờ -->
            <waiting-task
                ref="waitingTask"
                @update="(val: any) => updateWaitingTask(val)" />

            <!-- MODAL -->
            <hts-modal
                :show="showModalSolutionWaitingTimeTask && numberOfViolations !== 1"
                title="Điều chỉnh thông tin vận chuyển"
                @hide="closeSolutionWaitingTimeTask">
                <div class="mt-5">
                    <p>
                        Đơn hàng đang vi phạm nguyên tắc vận chuyển. Bạn phải điều chỉnh thông tin vận chuyển hoặc lên kế hoạch thủ công
                    </p>
                    <h4>
                        Vui lòng chọn giải pháp bên dưới
                    </h4>

                    <hts-form-radio
                        v-model="typeSolutionWaitingTimeTaskSelectedTmp"
                        class="w-full"
                        value-key="key"
                        label-key="solutionText"
                        type="list"
                        :items="solutionWaitingTimeTask" />
                </div>

                <div class="flex justify-end mt-5">
                    <hts-form-button
                        icon-name="hi-check-icon"
                        icon-position="right"
                        color="primary"
                        type="button"
                        :outline="false"
                        @click="confirmSolutionWaitingTimeTask">
                        {{ $t('labels.save') }}
                    </hts-form-button>
                </div>
            </hts-modal>

            <hts-modal
                :show="showModalSolutionWaitingTimeTask && numberOfViolations === 1"
                title="Điều chỉnh thông tin vận chuyển"
                @hide="resetViolations">
                <div class="mt-5">
                    <p>
                        Đơn hàng đang vi phạm nguyên tắc vận chuyển. Vui lòng kiểm tra lại các thời hạn đã đúng theo thông tin booking hoặc thực hiện điều chỉnh thời gian
                        di chuyển/thời gian hoàn thành tác vụ.
                    </p>
                </div>

                <div class="flex justify-end mt-5">
                    <hts-form-button
                        icon-position="right"
                        color="primary"
                        type="button"
                        :outline="false"
                        @click="resetViolations">
                        Kiểm tra thời hạn
                    </hts-form-button>

                    <hts-form-button
                        icon-position="right"
                        color="primary"
                        type="button"
                        :outline="false"
                        @click="confirmSolutionWaitingTimeTask">
                        Điều chỉnh thời gian
                    </hts-form-button>
                </div>
            </hts-modal>

            <hts-modal
                :show="manyPermanent"
                @hide="manyPermanent = false">
                <div>
                    <p>
                        Không thể tối ưu thời gian chờ do tại điểm có nhiều hơn 1 cố định
                    </p>
                </div>
            </hts-modal>

            <hts-modal
                :show="showViolatesPrinciples"
                @hide="showViolatesPrinciples = false">
                <div>
                    <p>
                        Đơn hàng đang vi phạm nguyên tắc vận chuyển
                    </p>
                    <p>
                        Bạn nên điều chỉnh thông tin vận chuyển hoặc tiếp tục lưu thông tin vi phạm
                    </p>
                </div>

                <div class="flex justify-end mt-5 gap-5">
                    <hts-form-button
                        icon-name="hi-check-icon"
                        icon-position="right"
                        type="button"
                        :outline="false"
                        @click="handleSubmitViolatesPrinciples">
                        {{ $t('labels.save') }}
                    </hts-form-button>

                    <hts-form-button
                        icon-name="hi-pencil-icon"
                        icon-position="right"
                        color="primary"
                        type="button"
                        :outline="false"
                        @click="handleAdjustShippingInformation">
                        {{ $t('labels.adjust') }}
                    </hts-form-button>
                </div>
            </hts-modal>

            <hts-modal
                :show="showWarningHolidayWorking"
                type="confirm"
                :confirm-label="$t('labels.continue')"
                @hide="() => actionCancelTimeWorking()"
                @confirm="() => actionConfirmTimeWorking()">
                <div>
                    <p>
                        {{ contentWarningHolidayWorking }}
                    </p>
                </div>
            </hts-modal>

            <hts-modal
                :show="showWarningOverTimeWorking"
                type="confirm"
                :confirm-label="$t('labels.continue')"
                @hide="() => actionCancelTimeWorking()"
                @confirm="() => actionConfirmTimeWorking()">
                <div>
                    <p>
                        {{ contentWarningOverTimeWorking }}
                    </p>
                </div>
            </hts-modal>

            <hts-modal
                :show="showConfirmEditShippingPlan"
                type="confirm"
                @hide="showConfirmEditShippingPlan = false"
                @confirm="handleCloseShippingPlan(true)">
                <div>
                    <p>
                        {{ $t('labels.confirmEditShippingPlan') }}
                    </p>
                </div>
            </hts-modal>
        </div>
    </hts-drawer>
</template>
<script setup lang="ts">
import Direction from '../plugins/direction'
// Xem và nhập Tác vụ góc
import ShippingInformation from '../shipping-information.vue'
// Xem thời gian tính từ tác vụ gốc, thời gian tổng hợp, thời gian tối ưu.
import DateTime from '../component/date-time.vue'
import ConditionSetting from '../condition-setting.vue'
import ShippingPlan from '../shipping-plan.vue'
import TravelDetail from '../travel-detail.vue'
import WaitingTask from '../waiting-task.vue'

const visible = ref(false)
const order = ref<{ [key: string]: any }>({})
const indexAlloment = ref<number>(0)
const notResetFields = ref(false)

const emits = defineEmits(['updateShippingPlan'])

const {
  // ref HTML element
  shippingPlan,
  travelDetail,
  waitingTask,
  conditionSetting,

  dataAdd,
  dataAddByCustomerRequest,
  viewShippingPlan,
  dataContact,
  dataLogisticContact,
  workflowPosition,
  customerRequestPages,
  loadShippingPlan,
  numberWarehouseNode,
  solutionWaitingTimeTask,
  showModalSolutionWaitingTimeTask,
  typeSolutionWaitingTimeTaskSelected,
  typeSolutionWaitingTimeTaskSelectedTmp,
  optimizeWaitingTime,
  manyPermanent,
  violatesPrinciples,
  showViolatesPrinciples,
  manualPlanning,
  resetViolations,
  numberOfViolations,
  showConfirmEditShippingPlan,
  contentWarningHolidayWorking,
  contentWarningOverTimeWorking,
  showWarningHolidayWorking,
  showWarningOverTimeWorking,
  actionCancelTimeWorking,
  actionConfirmTimeWorking,
  quoteLists,
  CEditTravelTime,
  CPositions,
  CDisabledEditCompletionTime,
  openWorkflowPosition,
  resetFields,
  resetNumberOfViolations,
  getNodeData,
  quoteChange,
  handleShowShippingPlan,
  handleEditShippingPlan,
  handleCloseShippingPlan,
  openConditionSetting,
  updateConditionSetting,
  openWaitingTask,
  updateWaitingTask,
  selectContact,
  selectLogisticContact,
  selectCooperatorBusinessOpportunity,
  confirmSolutionWaitingTimeTask,
  nextPage,
  CConfigCont,
  CConfigVehicle,
  viewPosition,
  openWithMap,
  closeSolutionWaitingTimeTask,
  updatePosition,
  customerChange,
  changeCustomerRequestPages,
  setQuoteData,
  handleAdjustShippingInformation,
  quickShowModalSolutionWaitingTimeTask,
  changeRouteToOrder,
  loading,
  updateWarningTimeWorking,
  listLabelCont,
  notFoundRoute,
  routeGGMap
} = Direction()

const open = async (orderValue: { [key: string]: any }, index: number, data: { [key: string]: any } | null = null) => {
  // set data
  await setData($lodash.cloneDeep(orderValue), $lodash.cloneDeep(data))
  indexAlloment.value = index

  // show UI
  loading.value = false
  visible.value = true

  // setTimeout(() => {
  //   if (!data?.positions && !data?.positions.length) {
  //     const { shippingPlanStatus } = orderValue

  //     if (shippingPlanStatus === 200 || shippingPlanStatus === 202) {
  //       if (shippingPlan?.value) {
  //         shippingPlan.value.show(dataAdd.value.positions, dataAdd.value)
  //       }
  //       viewShippingPlan.value = true
  //     }

  //     if (shippingPlanStatus === 200) {
  //       if (travelDetail.value) {
  //         travelDetail.value.show(orderValue)
  //       }
  //     }
  //   } else {
  //     handleShowShippingPlan()
  //   }

  //   loading.value = false
  // }, 1000)
}

const setData = async (orderValue: { [key: string]: any }, data: { [key: string]: any } | null = null) => {
  if (orderValue.createOrderFrom === 'QUOTE') {
    dataAdd.value = orderValue as any

    if (data) {
      dataAdd.value.positions = data.positions
    }
    await getNodeData()
    updateWarningTimeWorking(dataAdd.value.positions)
  }
  if (orderValue.createOrderFrom === 'CUSTOMER_REQUEST') {
    dataAdd.value = orderValue as any
    dataAddByCustomerRequest.value = orderValue as any

    if (data) {
      dataAddByCustomerRequest.value.positions = data.positions
      dataAdd.value.positions = data.positions
    }

    await getNodeData()

    updateWarningTimeWorking(dataAddByCustomerRequest.value.positions)
    if (orderValue.transportation && orderValue.transportation === 'CONTAINER') {
      dataAddByCustomerRequest.value.contName = listLabelCont[orderValue.contCategory] ?? ''
    }

    dataAdd.value.customer = orderValue.customer
    dataAdd.value.customerName = orderValue.customerName
    dataAdd.value.customerAbbreviationName = orderValue.customerAbbreviationName
    dataAdd.value.customerAddress = orderValue.customerAddress
    dataAdd.value.contact = orderValue.contact
    dataAdd.value.contactName = orderValue.contactName
    dataAdd.value.logisticContact = orderValue.logisticContact
    dataAdd.value.logisticContactName = orderValue.logisticContactName
    dataAdd.value.createOrderFrom = orderValue.createOrderFrom
    dataAdd.value.productivityOrder = orderValue.productivityOrder
    dataAdd.value.workingTimeRequest = orderValue.workingTimeRequest
    dataAdd.value.routingPositions = orderValue.routingPositions

    console.log('dataAdd.value.routingPositions', dataAdd.value.routingPositions)

    notResetFields.value = true
  }

  if (data) {
    manualPlanning.value = data.manualPlanning
    typeSolutionWaitingTimeTaskSelected.value = data.solution
    dataAdd.value.condition = data.condition || []
    dataAdd.value.waitingTasks = data.waitingTasks || []
  } else {
    manualPlanning.value = orderValue.manualPlanning || {
      IN: null,
      OUT: null
    }
    typeSolutionWaitingTimeTaskSelected.value = orderValue.solution || ''
    dataAdd.value.condition = orderValue.condition || []
    dataAdd.value.waitingTasks = orderValue.waitingTasks || []
  }

  // vô đc phân bổ thì chắc chắn đã có route
  notFoundRoute.value = false
  routeGGMap.value = false
}

const close = () => {
  // reset data
  reset()
  handleCloseShippingPlan()
  // close UI
  loading.value = true
  visible.value = false
}

const reset = () => {
  order.value = {}
}

const handleSubmitViolatesPrinciples = () => {
  violatesPrinciples.value = true
  showViolatesPrinciples.value = false
}

const handleSaveShipingPlan = async () => {
  const body = getBody()
  if (!body) {
    return
  }

  const api = $urlPartner.order.order['create-teamplate-shipping-plan']

  const data: any = await $api(api, {
    body
  })

  const resData = data?.data?.value
  if (resData?.success) {
    const code = resData.data.code
    const travelDetails = resData.data.travelDetails

    if (travelDetails && travelDetails.length) {
      process.client && $toast().success('Điều chỉnh kế hoạch thành công')
      emits('updateShippingPlan', {
        index: indexAlloment.value,
        solution: resData.data.solution,
        positions: resData.data.positions,
        manualPlanning: resData.data.manualPlanning,
        waitingTasks: dataAdd.value.waitingTasks,
        condition: dataAdd.value.condition
      })
      close()
      return
    }

    if (code === 201) {
      if (!numberOfViolations.value || numberOfViolations.value === 1) {
        numberOfViolations.value++
        showModalSolutionWaitingTimeTask.value = true
      } else {
        numberOfViolations.value++
        solutionWaitingTimeTask.value = resData.data.solutions
        showModalSolutionWaitingTimeTask.value = true
        typeSolutionWaitingTimeTaskSelected.value = ''
        typeSolutionWaitingTimeTaskSelectedTmp.value = ''
      }
      return
    }

    process.client && $toast().success('Điều chỉnh kế hoạch không thành công')
  }
}

const getBody = () => {
  let body = {}

  if (typeSolutionWaitingTimeTaskSelected.value === 'manualPlanning') {
    if (!manualPlanning.value.IN) {
      process.client && $toast().error(`Vui lòng nhập thời gian cho vị trí IN của ${CPositions.value?.[0]?.nodeLocationName} (vị trí 1)`)
      return
    }

    if (typeof manualPlanning.value.IN !== 'number') {
      process.client && $toast().error(`Vui lòng nhập thời gian cho vị trí IN của ${CPositions.value?.[0]?.nodeLocationName} (vị trí 1)`)
      return
    }

    body = {
      solution: 'manualPlanning',
      positions: dataAdd.value.createOrderFrom === 'QUOTE' ? dataAdd.value.positions : dataAddByCustomerRequest.value.positions,
      workingTimeRequest: dataAdd.value.workingTimeRequest || [],
      updateNode: true,
      waitingTasks: dataAdd.value.waitingTasks || [],
      manualPlanning: manualPlanning.value
    }
  } else {
    body = {
      positions: dataAdd.value.createOrderFrom === 'QUOTE' ? dataAdd.value.positions : dataAddByCustomerRequest.value.positions,
      workingTimeRequest: dataAdd.value.workingTimeRequest || [],
      updateNode: true,
      waitingTasks: dataAdd.value.waitingTasks || []
    }
  }

  return body
}

defineExpose({ open })
</script>
